import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { TableColumn } from "react-data-table-component";
import { FormProvider, useForm } from "react-hook-form";
import { BiSolidPencil } from "react-icons/bi";
import { FaRegTrashAlt } from "react-icons/fa";
import addico from "../../assets/images/add.svg";
import crossico from "../../assets/images/close.svg";
import searchico from "../../assets/images/search.svg";
import { ESDataTable } from "../../controls/ESDataTable";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import ESTooltip from "../../controls/ESTooltip";
import ESAutocompleteSearch from "../../controls/ESAutocompleteSearch";
import { UserContext } from "../../routes/routes";
import {
  deleteUserDesignation,
  getAllUserDesignations,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import {
  Option,
  PageRecordLimit,
  checkIfSuperAdmin,
  formatDateCommon,
  getCachedData,
  handlePaginationAfterDelete,
  queryClient,
  setPageRecordLimit
} from "../../utils/common";
import { ToastTypeCode, showToast } from "../../utils/Constant";
import ExportExcel from "../Common/ExportExcel/ExportExcel";
import Footer from "../footer/Footer";
import Header from "../header/Header";
import Loader from "../loader/Loader";
import ConfirmationPopup from "../popUp/ConfirmationPopup";
import SubHeader from "../subheader/Subheader";
import AddUserDesignation from "./AddUserDesignation";
import "./UserDesignationList.scss";
interface UserDesignation {
  DesignationId: number;
  DesignationName: string;
  DesignationDescription: string;
  IsActive: number;
  CreateUserId: number;
  UpdateUserId: number;
  CreateDate: string;
  UpdateDate: string;
  InternalKey: string;
  ConcurrencyKey: string;
  OrganizationId: number;
}

interface CustomTableColumn<T> extends TableColumn<T> {
  onClick?: () => void;
}

const UserDesignationList: React.FC = () => {
  const [userDesignations, setUserDesignations] = useState<UserDesignation[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PageRecordLimit);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState<string>("CreateDate");
  const [sortDir, setSortDir] = useState<string>("DESC");
  const [searchText, setSearchText] = useState<string>("");
  const [showPopup, setShowPopup] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const methods = useForm();
  const [organizationId, setOrganizationId] = useState<number | 0>(0);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { appContextdata } = useContext<any>(UserContext);
  const [organizationDropDownChange, setOrganizationDropDownChange] =
    useState(false);
  const [defaultOraganizationDropDown, setDefaultOraganizationDropDown] =
    useState<any>(null);
  const [isAddIconVisible, setIsAddIconVisible] = useState(true);

  const fetchUserDesignations = async (organizationidFromProps?: any, searchOverride?: string) => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await axiosPost(getAllUserDesignations, {
        SearchText: searchOverride !== undefined ? searchOverride : searchText,
        IsActive: false,
        CurrentPageNo: searchOverride !== undefined ? 1 : currentPage,
        PageSize: pageSize,
        SortDir: sortDir,
        SortBy: sortBy,
        OrganizationId: organizationidFromProps
          ? organizationidFromProps
          : organizationId === 0
            ? -1
            : organizationId,
      });
      const data = response?.data?.ExtraData;
      setUserDesignations(data);
      setTotalRecords(data?.length > 0 ? data[0].TotalRecords : 0);
    } catch (error) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditId(id);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditId(null);
    setShowForm(true);
    setIsAddIconVisible(false);
  };

  const handleFormClose = () => {
    setShowForm(false);
    fetchUserDesignations();
    setIsAddIconVisible(true);
  };

  const handleDelete = async (id: number) => {
    if (id) {
      try {
        setLoading(true);
        const response: any = await axiosPost(deleteUserDesignation, {
          DesignationId: id,
        });
        if (response?.data?.Status === 200) {
          setUserDesignations((prevState) =>
            prevState.filter((item) => item.DesignationId !== id)
          );
          queryClient.invalidateQueries("designation");
          showToast(
            "User designation deleted successfully",
            ToastTypeCode?.SUCCESS
          );
          handlePaginationAfterDelete(
            response?.data?.Status,
            pageSize,
            currentPage,
            setCurrentPage,
            totalRecords
          );
        } else {
          showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
        }
        setShowPopup(false);
        fetchUserDesignations();
      } catch (error) {
        showToast("Error deleting user designation", ToastTypeCode?.ERROR);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (column: any, sortDirection: "asc" | "desc") => {
    if (Object.keys(column)?.length === 0 && column.constructor === Object) {
      return;
    }
    setSortBy(column.sortField);
    setSortDir(sortDirection.toUpperCase());
  };

  const handleSearchTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchText(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = (searchOverride?: string) => {
    setCurrentPage(1);
    fetchUserDesignations(undefined, searchOverride);
  };

  const handlePerRowsChange = async (newPerPage: any, page: any) => {
    setPageSize(newPerPage);
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowPopup(true);
  };

  useEffect(() => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (!isAdmin) {
        fetchUserDesignations(appContextdata?.OrganizationId);
        setPageRecordLimit(pageSize);
      }
    }
  }, [appContextdata?.Role, currentPage, pageSize, sortBy, sortDir]);

  const handleOrganizationChange = (selectedOption: Option | null) => {
    setOrganizationId(selectedOption?.value ?? 0);
    setCurrentPage(1);
    setOrganizationDropDownChange(true);
    setDefaultOraganizationDropDown(null);
  };

  useEffect(() => {
    if (organizationDropDownChange) {
      fetchUserDesignations();
      setPageRecordLimit(pageSize);
    }
  }, [organizationId, currentPage, pageSize, sortBy, sortDir, organizationDropDownChange]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);

      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData("organizations")) as Option[];

        setOrganizations(options);
        const defaultOption: any = options?.find(
          (option) => option.value === appContextdata?.OrganizationId
        );
        handleOrganizationChange(defaultOption);
        setDefaultOraganizationDropDown(defaultOption);
      }
    }
  };
  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const columns: CustomTableColumn<UserDesignation>[] = [
    {
      name: "Actions",
      cell: (row) => (
        <>
          <ESTooltip text="Edit Designation" placement="top">
            <button
              onClick={() => handleEdit(row.DesignationId)}
              className="btnicon"
            >
              <BiSolidPencil color="#529c25" size={14} data-tour="edit-designation" />
            </button>
          </ESTooltip>
          <ESTooltip text="Delete Designation" placement="top">
            <button
              onClick={() => confirmDelete(row.DesignationId)}
              className="btnicon"
            ><FaRegTrashAlt color="#ff3610" size={14} data-tour="delete-designation" />

            </button>
          </ESTooltip>
        </>
      ),
      grow: 1,
      width: "80px",
    },
    {
      name: "Designation Name",
      selector: (row) => row.DesignationName,
      sortable: true,
      sortField: "DesignationName",
      grow: 2,
    },
    {
      name: "Description",
      selector: (row) => row.DesignationDescription,
      sortable: true,
      sortField: "DesignationDescription",
      grow: 4,
    },
    {
      name: "Last Modified",
      selector: (row) => formatDateCommon(row.CreateDate),
      sortable: true,
      sortField: "UpdateDate",
      grow: 1,
    },
    {
      name: "Is Active",
      selector: (row) => (row?.IsActive ? "Yes" : "No"),
      sortable: true,
      sortField: "IsActive",
    },
  ];

  return (
    <div className="wrap-userdesignation">
      <Header />
      <SubHeader data={userDesignations} isAddIconVisible={isAddIconVisible} />
      <div className="userdesignation-blk">
        <Container fluid>
          <Card>
            <CardHeader>
              <Row>
                <Col md={6}>
                  <h2 className="page-title">User Designations</h2>
                </Col>
                <Col md={6}>
                  <div className="search-userdesignation">
                    {isSuperAdmin && (
                      <FormProvider {...methods}>
                        {organizations && organizations?.length >= 1 && (
                          <ESSearchDropdown
                            name="OrganizationId"
                            placeholder="Select Organization"
                            options={organizations}
                            onChange={handleOrganizationChange}
                            defaultValue={defaultOraganizationDropDown}
                          />
                        )}
                      </FormProvider>
                    )}

                    <div style={{ flexGrow: 1, minWidth: '200px' }} className="ms-2">
                      <ESAutocompleteSearch
                        apiUrl={getAllUserDesignations}
                        labelKey="DesignationName"
                        valueKey="DesignationId"
                        initialValue={searchText}
                        onSearchChange={(text) => setSearchText(text)}
                        onSelect={(item) => {
                          setSearchText(item.DesignationName);
                          handleSearch(item.DesignationName);
                        }}
                        onSubmit={() => handleSearch()}
                        otherParams={{
                          IsActive: false,
                          OrganizationId: organizationId === 0 ? -1 : organizationId,
                        }}
                      />
                    </div>
                    <div className="d-flex">
                      <ExportExcel
                        searchText={searchText}
                        model={userDesignations}
                        componentName={"UserDesignations"}
                        fileName={"UserDesignations"}
                      ></ExportExcel>
                      <ESTooltip
                        text={showForm ? "Close" : "Add Designation"}
                        placement="top"
                      >
                        <Button
                          onClick={showForm ? handleFormClose : handleCreate}
                          className="primary-btn btn-add"
                          data-tour="add-designation"
                        >
                          {showForm ? (
                            <img
                              src={crossico}
                              className="closeico"
                              alt="close"
                            />
                          ) : (
                            <img src={addico} alt="add" />
                          )}
                        </Button>
                      </ESTooltip>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {showForm && (
                <AddUserDesignation
                  designationId={editId}
                  onClose={handleFormClose}
                />
              )}
              {error && <p>{error}</p>}
              <div className="table-responsive">
                <ESDataTable
                  columns={columns}
                  data={userDesignations}
                  paginationTotalRows={totalRecords}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  onChangeRowsPerPage={handlePerRowsChange}
                />
              </div>
              {showPopup && (
                <ConfirmationPopup
                  show={showPopup}
                  message="Are you sure you want to delete this designation?"
                  onConfirm={() => handleDelete(deleteId)}
                  onCancel={() => setShowPopup(false)}
                />
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
      <Footer />
      {loading ? <Loader /> : null}
    </div>
  );
};

export default UserDesignationList;
